<template>
  <div class="page-content">
    <!-- Header -->
    <div class="page-header">
      <div>
        <BreadcrumbComponent :items="items" />
        <h1 class="page-title">Produits</h1>
        <p class="page-subtitle">{{ rows.length }} produit{{ rows.length > 1 ? 's' : '' }}</p>
      </div>
      <v-btn color="primary" depressed @click="openNew">
        <v-icon left small>mdi-plus</v-icon>
        Nouveau produit
      </v-btn>
    </div>

    <PreloaderComponent :enable="enable" />

    <!-- Search -->
    <div v-if="enable" class="d-flex justify-end mb-3">
      <input v-model="search" type="text" placeholder="Rechercher..." class="search-input" />
    </div>

    <!-- Hidden file input for image upload -->
    <input type="file" ref="file" style="display: none" @change="uploadFile()" accept="image/*">

    <!-- Data table -->
    <v-card flat v-if="enable" class="table-card">
      <v-data-table
        :headers="tableHeaders"
        :items="rows"
        :items-per-page="15"
        :search="search"
        no-data-text="Aucun produit trouvé"
        :footer-props="{ 'items-per-page-text': 'Par page', 'items-per-page-options': [10, 15, 25, 50, -1] }"
      >
        <template v-slot:item.image_url="{ item }">
          <div class="product-img" @click="editFile(item)">
            <img v-if="item.image_url" :src="'https://keslakay.s3.us-east-1.amazonaws.com/' + item.image_url" />
            <v-icon v-else small color="grey lighten-1">mdi-image-outline</v-icon>
          </div>
        </template>
        <template v-slot:item.name="{ item }">
          <v-tooltip bottom open-delay="200" max-width="320" content-class="barcode-tooltip-outer">
            <template v-slot:activator="{ on, attrs }">
              <span
                v-bind="attrs"
                v-on="on"
                class="font-weight-medium product-name-with-barcode"
              >{{ item.name }}</span>
            </template>
            <div class="barcode-tooltip-inner">
              <span class="barcode-tooltip-label">Code-barres</span>
              <span class="barcode-tooltip-value">{{ item.barcode || '—' }}</span>
            </div>
          </v-tooltip>
        </template>
        <template v-slot:item.cost="{ item }">
          {{ item.cost | formatNumber }} {{ item.rate.name }}
        </template>
        <template v-slot:item.price="{ item }">
          {{ item.price | formatNumber }} {{ item.rate.name }}
        </template>
        <template v-slot:item.price_usd_equiv="{ item }">
          {{ formatUsdCell(item, 'price') }}
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip x-small :color="item.status === false ? 'error' : 'success'" dark>
            {{ item.status === false ? 'Inactif' : 'Actif' }}
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn v-if="false" icon x-small @click="edit_stock(item)" title="Stock" class="mr-1">
            <v-icon small color="secondary">mdi-cube-outline</v-icon>
          </v-btn>
          <v-btn v-if="composedEnabled" icon x-small @click="open_composition(item)" title="Composition" class="mr-1">
            <v-icon small color="orange darken-2">mdi-food-variant</v-icon>
          </v-btn>
          <v-btn icon x-small @click="edit(item)" title="Modifier">
            <v-icon small color="primary">mdi-pencil</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- Product create/edit dialog -->
    <v-dialog v-model="dialog" scrollable max-width="560" @click:outside="close">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title">
          <v-icon color="primary" size="18" class="mr-2">{{ index > -1 ? 'mdi-pencil' : 'mdi-plus-circle' }}</v-icon>
          {{ form_title }}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="dialog-body">
          <div class="form-group">
            <label class="form-label">Nom *</label>
            <v-text-field v-model="product.name" outlined dense hide-details="auto" :rules="[v => !!v || 'Requis']" placeholder="Nom du produit"></v-text-field>
          </div>

          <v-row>
            <v-col cols="12" sm="6">
              <div class="form-group">
                <label class="form-label">Code barre</label>
                <v-text-field v-model="product.barcode" outlined dense hide-details placeholder="Optionnel"></v-text-field>
              </div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="form-group">
                <label class="form-label">Catégorie *</label>
                <v-select v-model="product.category_id" :items="categories" item-text="name" item-value="id" outlined dense hide-details="auto" :rules="[v => !!v || 'Requis']" placeholder="Sélectionner"></v-select>
              </div>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="6">
              <div class="form-group">
                <label class="form-label">Coût (HTG) *</label>
                <v-text-field
                  v-model="product.cost"
                  type="number"
                  outlined
                  dense
                  hide-details="auto"
                  :rules="[v => v !== '' || 'Requis']"
                  placeholder="0.00"
                  @input="onHtgCostInput"
                ></v-text-field>
              </div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="form-group">
                <label class="form-label">Prix de vente (HTG) *</label>
                <v-text-field
                  v-model="product.price"
                  type="number"
                  outlined
                  dense
                  hide-details="auto"
                  :rules="[v => v !== '' || 'Requis']"
                  placeholder="0.00"
                  @input="onHtgPriceInput"
                ></v-text-field>
              </div>
            </v-col>
          </v-row>

          <p v-if="hasUsdStoresForProducts && usdSalesRateForProducts > 0" class="usd-sync-hint">
            Les montants sont <strong>enregistrés en gourdes (HTG)</strong>. Vous pouvez saisir le HTG ou l’USD : les champs se mettent à jour en direct (taux du jour : <strong>1 USD = {{ usdSalesRateForProducts | formatNumber }} HTG</strong>).
          </p>
          <v-alert v-else-if="hasUsdStoresForProducts" type="info" dense text class="mb-2 py-1">
            Taux USD / HTG indisponible : saisissez uniquement les montants en gourdes.
          </v-alert>

          <v-row v-if="hasUsdStoresForProducts">
            <v-col cols="12" sm="6">
              <div class="form-group">
                <label class="form-label">Coût (USD)</label>
                <v-text-field
                  v-model="costUsdDraft"
                  type="text"
                  inputmode="decimal"
                  outlined
                  dense
                  hide-details
                  placeholder="0.00"
                  :disabled="!usdSalesRateForProducts"
                  @input="onUsdCostInput"
                  @blur="onUsdCostBlur"
                ></v-text-field>
              </div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="form-group">
                <label class="form-label">Prix de vente (USD)</label>
                <v-text-field
                  v-model="priceUsdDraft"
                  type="text"
                  inputmode="decimal"
                  outlined
                  dense
                  hide-details
                  placeholder="0.00"
                  :disabled="!usdSalesRateForProducts"
                  @input="onUsdPriceInput"
                  @blur="onUsdPriceBlur"
                ></v-text-field>
              </div>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="6" v-if="min_price">
              <div class="form-group">
                <label class="form-label">Prix minimum</label>
                <v-text-field v-model="product.min_price" type="number" outlined dense hide-details placeholder="0.00"></v-text-field>
              </div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="form-group">
                <label class="form-label">Seuil d'alerte (stock faible)</label>
                <v-text-field v-model.number="product.stock_alert" type="number" min="0" outlined dense hide-details placeholder="0"></v-text-field>
              </div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="form-group">
                <label class="form-label">Statut</label>
                <v-select v-model="product.status" :items="statusOptions" item-text="name" item-value="id" outlined dense hide-details></v-select>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text @click="close" class="mr-1">Annuler</v-btn>
          <v-btn color="primary" depressed :loading="loading" :disabled="loading || !product.name || !product.category_id" @click="save">
            {{ index > -1 ? 'Enregistrer' : 'Créer' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Stock operation dialog -->
    <v-dialog v-model="dialog_stock" max-width="480" @click:outside="close_stock">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title">
          <v-icon color="secondary" size="18" class="mr-2">mdi-cube-outline</v-icon>
          {{ form_title_stock }}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="dialog-body">
          <div class="form-group">
            <label class="form-label">Type *</label>
            <v-select v-model="stock.type" :items="types" item-text="name" item-value="id" outlined dense hide-details></v-select>
          </div>

          <div class="form-group">
            <label class="form-label">Quantité *</label>
            <v-text-field v-model="stock.quantity" type="number" outlined dense hide-details placeholder="0"></v-text-field>
          </div>

          <v-row>
            <v-col cols="12" sm="6">
              <div class="form-group">
                <label class="form-label">Coût</label>
                <v-text-field v-model="stock.cost" type="number" outlined dense hide-details placeholder="0.00"></v-text-field>
              </div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="form-group">
                <label class="form-label">Prix</label>
                <v-text-field v-model="product.price" type="number" outlined dense hide-details placeholder="0.00"></v-text-field>
              </div>
            </v-col>
          </v-row>

          <div class="form-group" v-if="min_price">
            <label class="form-label">Prix minimum</label>
            <v-text-field v-model="product.min_price" type="number" outlined dense hide-details placeholder="0.00"></v-text-field>
          </div>

          <div class="form-group">
            <label class="form-label">Mémo</label>
            <v-text-field v-model="stock.memo" outlined dense hide-details placeholder="Optionnel"></v-text-field>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text @click="close_stock" class="mr-1">Annuler</v-btn>
          <v-btn color="primary" depressed :loading="loading" :disabled="loading" @click="save_stock">
            Valider
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Product composition dialog -->
    <v-dialog v-model="dialog_composition" max-width="640" @click:outside="close_composition">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title">
          <v-icon color="orange darken-2" size="18" class="mr-2">mdi-food-variant</v-icon>
          Composition : {{ composition_product ? composition_product.name : '' }}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="dialog-body">
          <v-simple-table dense>
            <thead>
              <tr>
                <th class="text-left">INGRÉDIENT</th>
                <th class="text-right" style="width: 130px">QUANTITÉ</th>
                <th class="text-left" style="width: 110px">UNITÉ</th>
                <th style="width: 50px"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in composition_rows" :key="idx">
                <td>
                  <v-select v-model="row.ingredient_id" :items="composition_ingredients" item-text="name" item-value="id" outlined dense hide-details></v-select>
                </td>
                <td>
                  <v-text-field v-model.number="row.quantity" type="number" min="0" step="0.01" outlined dense hide-details reverse></v-text-field>
                </td>
                <td>
                  <v-select v-model="row.unit" :items="composition_units" outlined dense hide-details></v-select>
                </td>
                <td class="text-center">
                  <v-btn icon x-small @click="composition_remove(idx)"><v-icon small color="error">mdi-delete</v-icon></v-btn>
                </td>
              </tr>
              <tr v-if="composition_rows.length === 0">
                <td colspan="4" class="text-center text--secondary pa-6">Aucun ingrédient lié</td>
              </tr>
            </tbody>
          </v-simple-table>
          <div class="mt-3">
            <v-btn small text color="primary" @click="composition_add"><v-icon left small>mdi-plus</v-icon>Ajouter un ingrédient</v-btn>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text @click="close_composition">Annuler</v-btn>
          <v-btn color="primary" depressed :loading="composition_saving" :disabled="composition_saving || !composition_can_save" @click="save_composition">Enregistrer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <FlashMessage :position="'right bottom'"></FlashMessage>
  </div>
</template>

<script>
import BreadcrumbComponent from '../components/BreadcrumbComponent.vue'
import PreloaderComponent from '../components/PreloaderComponent.vue'
import { amountToUsd, persistUsdContext, readUsdContext } from '../utils/usdPersistence'

export default {
  name: 'ProductsView',
  components: { BreadcrumbComponent, PreloaderComponent },
  data: () => ({
    search: '',
    file_index: -1,
    file: { product_id: -1, image: [] },
    statusOptions: [{ name: 'Actif', id: true }, { name: 'Inactif', id: false }],
    index: -1,
    store_id: '',
    types: [{ name: 'Réception', id: 1 }, { name: 'Ajustement', id: 2 }, { name: 'Autre', id: 3 }],
    rate_id: '',
    rate: '',
    hasUsdStoresForProducts: false,
    usdSalesRateForProducts: 0,
    loading: false,
    business_id: 0,
    min_price: false,
    form_title: 'Nouveau Produit',
    form_title_stock: 'Nouvelle Opération',
    items: [{ text: 'Produits', disabled: false, href: '/products' }],
    enable: false,
    rows: [],
    dialog: false,
    dialog_stock: false,
    categories: [],
    product: { name: '', barcode: '', min_price: '', cost: '', price: '', stock_alert: 0, rate_id: 1, groupe_id: 1, category_id: '', status: true },
    stock: { product_id: '', user_id: '', cost: '', memo: '', type: 1, quantity: '' },
    costUsdDraft: '',
    priceUsdDraft: '',
    composedEnabled: false,
    dialog_composition: false,
    composition_product: null,
    composition_rows: [],
    composition_ingredients: [],
    composition_saving: false,
    composition_units: ['mg', 'g', 'kg', 't', 'lb', 'oz', 'ml', 'cl', 'dl', 'l', 'gal', 'qt', 'pt', 'floz', 'cup', 'tbsp', 'tsp', 'unité', 'paire', 'demi-douzaine', 'douzaine', 'centaine', 'millier'],
  }),

  computed: {
    tableHeaders() {
      const base = [
        { text: '', value: 'image_url', sortable: false, width: '50px' },
        { text: 'NOM', value: 'name', align: 'start' },
        { text: 'CATÉGORIE', value: 'category.name' },
        { text: 'COÛT', value: 'cost', align: 'end' },
        { text: 'PRIX', value: 'price', align: 'end' },
      ]
      if (this.hasUsdStoresForProducts) {
        base.push({ text: 'ÉQUIV. USD (prix)', value: 'price_usd_equiv', align: 'end' })
      }
      base.push(
        { text: 'STATUT', value: 'status', align: 'center', width: '90px' },
        { text: '', value: 'actions', sortable: false, width: '80px', align: 'end' },
      )
      return base
    },
    composition_can_save() {
      return this.composition_rows.every(function (r) { return r.ingredient_id && r.quantity > 0 && r.unit })
    },
  },

  watch: {
    dialog(val) {
      if (val) this.$nextTick(() => this.refreshUsdDrafts())
    },
  },

  methods: {
    /** Conversion modal : les champs coût/prix sont traités comme HTG pour l’équivalent USD (aligné sur l’enregistrement en gourdes). */
    refreshUsdDrafts() {
      var r = this.usdSalesRateForProducts
      if (!r || r <= 0) {
        this.costUsdDraft = ''
        this.priceUsdDraft = ''
        return
      }
      this.costUsdDraft = this.htgToUsdDraftString(this.product.cost, r)
      this.priceUsdDraft = this.htgToUsdDraftString(this.product.price, r)
    },
    htgToUsdDraftString(htgVal, rate) {
      var h = parseFloat(htgVal)
      if (isNaN(h) || !rate || rate <= 0) return ''
      var u = h / rate
      return this.formatUsdTwoDecimals(u)
    },
    roundUsdCents(n) {
      var x = Number(n)
      if (isNaN(x)) return NaN
      return Math.round((x + Number.EPSILON) * 100) / 100
    },
    /** Montants USD affichés / saisis : exactement 2 décimales (0.00). */
    formatUsdTwoDecimals(n) {
      var u = this.roundUsdCents(n)
      if (isNaN(u)) return ''
      return u.toFixed(2)
    },
    /** Chaîne saisie (FR: virgule) → nombre pour calcul ; ne modifie pas la chaîne affichée pendant la frappe. */
    parseUsdInputString(s) {
      var raw = String(s == null ? '' : s).trim().replace(/\s/g, '').replace(',', '.')
      if (raw === '' || raw === '-' || raw === '.' || raw === '-.') return null
      if (raw.slice(-1) === '.') return null
      var u = this.roundUsdCents(parseFloat(raw))
      return isNaN(u) ? null : u
    },
    /** Montant formulaire (HTG, etc.) : virgule / espaces → nombre pour l’API. */
    normalizeAmountForApi(val) {
      if (val === '' || val == null) return val
      var s = String(val).trim().replace(/\s/g, '').replace(',', '.')
      var n = parseFloat(s)
      return isNaN(n) ? val : n
    },
    onHtgCostInput() {
      var r = this.usdSalesRateForProducts
      if (!r || r <= 0) return
      this.costUsdDraft = this.htgToUsdDraftString(this.product.cost, r)
    },
    onHtgPriceInput() {
      var r = this.usdSalesRateForProducts
      if (!r || r <= 0) return
      this.priceUsdDraft = this.htgToUsdDraftString(this.product.price, r)
    },
    onUsdCostInput() {
      var r = this.usdSalesRateForProducts
      if (!r || r <= 0) return
      var u = this.parseUsdInputString(this.costUsdDraft)
      if (u == null) return
      var h = u * r
      this.product.cost = Math.round(h * 100) / 100
    },
    onUsdCostBlur() {
      var r = this.usdSalesRateForProducts
      if (!r || r <= 0) return
      var raw = String(this.costUsdDraft == null ? '' : this.costUsdDraft).trim()
      if (raw === '' || raw === '-' || raw === '.') return
      var u = this.parseUsdInputString(this.costUsdDraft)
      if (u == null) return
      this.costUsdDraft = this.formatUsdTwoDecimals(u)
      var h = u * r
      this.product.cost = Math.round(h * 100) / 100
    },
    onUsdPriceInput() {
      var r = this.usdSalesRateForProducts
      if (!r || r <= 0) return
      var u = this.parseUsdInputString(this.priceUsdDraft)
      if (u == null) return
      var h = u * r
      this.product.price = Math.round(h * 100) / 100
    },
    onUsdPriceBlur() {
      var r = this.usdSalesRateForProducts
      if (!r || r <= 0) return
      var raw = String(this.priceUsdDraft == null ? '' : this.priceUsdDraft).trim()
      if (raw === '' || raw === '-' || raw === '.') return
      var u = this.parseUsdInputString(this.priceUsdDraft)
      if (u == null) return
      this.priceUsdDraft = this.formatUsdTwoDecimals(u)
      var h = u * r
      this.product.price = Math.round(h * 100) / 100
    },
    formatUsdCell(item, field) {
      var rid = item.rate_id != null && item.rate_id !== '' ? item.rate_id : (item.rate && item.rate.id)
      if (rid == null || rid === '') rid = 1
      var u = amountToUsd(item[field], rid, this.usdSalesRateForProducts)
      if (u == null) return '—'
      return this.$options.filters.formatNumber(u) + ' USD'
    },
    load() {
      var self = this
      this.$axios.get('/products?include=category,rate,store_products&orderasc=name')
        .then(function (response) { self.rows = response.data; self.enable = true })
    },
    getCategories() {
      var self = this
      this.$axios.get('/categories').then(function (r) { self.categories = r.data })
    },
    editFile(item) {
      this.$refs.file.click()
      this.file.product_id = item.id
      this.file_index = this.rows.indexOf(item)
      this.product = Object.assign({}, item)
    },
    uploadFile() {
      var image = this.$refs.file.files[0]
      var self = this
      var formdata = new FormData()
      formdata.append('image', image)
      // En-tête par requête uniquement : modifier axios.defaults.headers rendait toutes les requêtes
      // suivantes multipart sans boundary (warning PHP en tête de réponse → JSON non parsé).
      this.$axios({ method: 'post', url: '/products/upload_image/' + this.product.id, data: formdata, headers: { 'Content-Type': 'multipart/form-data' } })
        .then(function (response) {
          Object.assign(self.rows[self.file_index], response.data)
          self.flashMessage.success({ title: 'Succès', message: 'Image enregistrée' })
        })
    },
    removeFile(item) {
      this.file_index = this.rows.indexOf(item)
      var self = this
      this.product = Object.assign({}, item)
      this.product.image_url = ''
      this.$axios.put('/products/' + this.product.id + '?include=category,rate,store_products&data=' + encodeURIComponent(JSON.stringify(self.product)))
        .then(function (response) { Object.assign(self.rows[self.file_index], response.data); self.dialog = false; self.loading = false; self.reset() })
    },
    openNew() { this.reset(); this.dialog = true },
    save(status) {
      if (!this.product.name || !this.product.category_id) {
        if (!status) return
      }
      var self = this
      this.loading = true
      // Prix coût/prix sur cette page = toujours en HTG (rate_id 1). Ne pas utiliser le rate du magasin
      // (magasin USD imposait rate_id 2 → montants HTG traités comme USD, colonne équivalent ×1).
      this.product.rate_id = 1

      this.product.cost = this.normalizeAmountForApi(this.product.cost)
      this.product.price = this.normalizeAmountForApi(this.product.price)
      if (this.min_price) {
        var minP = this.product.min_price
        if (minP === '' || minP == null) {
          this.product.min_price = 0
        } else {
          var minNorm = this.normalizeAmountForApi(minP)
          this.product.min_price = typeof minNorm === 'number' && !isNaN(minNorm) ? minNorm : 0
        }
      }

      var userId = 1
      try { var auth = JSON.parse(localStorage.authentication); if (auth && auth.user) userId = auth.user.id } catch (e) { /* default */ }
      this.product.user_id = userId

      var payload = 'data=' + encodeURIComponent(JSON.stringify(self.product))
      var productDataJson = JSON.stringify(self.product)

      if (this.index > -1) {
        var url = '/products/' + this.product.id + '?include=category,rate,store_products'
        for (var i = (this.product.store_products || []).length - 1; i >= 0; i--) {
          this.product.store_products[i].price = this.product.price
          this.product.store_products[i].cost = this.product.cost
        }
        this.$axios.put(url, payload, { params: { data: productDataJson } })
          .then(function (response) {
            var product = self.setStore(response.data)
            Object.assign(self.rows[self.index], product)
            self.reset_stock(); self.dialog = false; self.loading = false
            self.flashMessage.success({ title: 'Succès', message: 'Produit modifié' })
            self.reset()
          }).catch(function () { self.loading = false })
      } else {
        this.$axios.post('/products?include=category,rate,store_products', payload, { params: { data: productDataJson } })
          .then(function (response) {
            var product = self.setStore(response.data)
            self.rows.push(product)
            self.dialog = false; self.loading = false
            self.flashMessage.success({ title: 'Succès', message: 'Produit créé' })
            self.reset()
          }).catch(function () { self.loading = false })
      }
    },
    setStore(product) {
      var storeProduct = { store_id: this.store_id, product_id: product.id, price: product.price, cost: product.cost, min_price: product.min_price }
      this.$axios.post('/store-products?data=' + JSON.stringify(storeProduct))
        .then(function (response) { product.store_products = response.data }).catch(function () {})
      return product
    },
    edit(item) {
      this.index = this.rows.indexOf(item)
      this.product = Object.assign({}, item)
      this.form_title = 'Modifier : ' + this.product.name
      this.dialog = true
    },
    save_stock() {
      var self = this
      this.loading = true
      var user = 1
      try { user = JSON.parse(localStorage.authentication).user.id } catch (e) { /* default */ }
      this.stock.user_id = user
      this.stock.product_id = this.product.id
      this.$axios.post('/operations?data=' + JSON.stringify(self.stock))
        .then(function () {
          self.dialog_stock = false
          self.product.stock = parseFloat(self.product.stock) + parseFloat(self.stock.quantity)
          self.save(true)
          self.loading = false
        })
    },
    edit_stock(item) {
      this.index = this.rows.indexOf(item)
      this.stock.cost = item.cost
      this.product = Object.assign({}, item)
      this.form_title_stock = 'Opération : ' + this.product.name
      this.dialog_stock = true
    },
    close() { this.dialog = false; this.reset(); this.loading = false },
    close_stock() { this.dialog_stock = false; this.reset_stock(); this.loading = false },
    reset() {
      this.product = { name: '', barcode: '', min_price: '', cost: '', price: '', stock_alert: 0, rate_id: 1, groupe_id: 1, category_id: '', status: true }
      this.index = -1; this.form_title = 'Nouveau Produit'
      this.costUsdDraft = ''
      this.priceUsdDraft = ''
    },
    reset_stock() {
      this.stock = { product_id: '', user_id: '', cost: '', memo: '', type: 1, quantity: '' }
      this.index = -1; this.form_title_stock = 'Nouvelle Opération'
    },
    getStore() {
      this.business_id = JSON.parse(localStorage.business).id
      var self = this
      this.$axios.get('/stores?orderasc=name&include=store_rates&business_id=' + this.business_id)
        .then(function (response) {
          var stores = response.data
          self.hasUsdStoresForProducts = stores.some(function (s) { return Number(s.rate_id) === 2 })
          self.composedEnabled = stores.some(function (s) { return !!s.enable_composed_products })
          for (var i = stores.length - 1; i >= 0; i--) {
            self.store_id = stores[i].id
            self.rate_id = stores[i].rate_id
            self.min_price = stores[i].min_price
            for (var j = (stores[i].store_rates || []).length - 1; j >= 0; j--) {
              if (stores[i].store_rates[j].rate_id == 2) self.rate = stores[i].store_rates[j].sales_rate
            }
          }
          var rateNum = parseFloat(self.rate) || 0
          if (self.hasUsdStoresForProducts && !rateNum) {
            rateNum = readUsdContext().rate
          }
          self.usdSalesRateForProducts = rateNum
          if (self.hasUsdStoresForProducts && self.usdSalesRateForProducts) {
            persistUsdContext(true, self.usdSalesRateForProducts)
          } else if (!self.hasUsdStoresForProducts) {
            persistUsdContext(false, null)
          }
        })
    },
    open_composition(item) {
      this.composition_product = item
      this.composition_rows = []
      this.dialog_composition = true
      var self = this
      var ingredientsPromise = this.composition_ingredients.length
        ? Promise.resolve({ data: this.composition_ingredients })
        : this.$axios.get('/ingredients?orderasc=name')
      ingredientsPromise.then(function (r) {
        self.composition_ingredients = r.data || []
      })
      this.$axios.get('/product-compositions?product_id=' + item.id + '&include=ingredient&orderasc=id')
        .then(function (r) {
          self.composition_rows = (r.data || []).map(function (row) {
            return { ingredient_id: row.ingredient_id, quantity: parseFloat(row.quantity), unit: row.unit }
          })
        })
    },
    composition_add() { this.composition_rows.push({ ingredient_id: null, quantity: 1, unit: 'g' }) },
    composition_remove(idx) { this.composition_rows.splice(idx, 1) },
    save_composition() {
      if (!this.composition_product) return
      var self = this; this.composition_saving = true
      this.$axios.post('/product-compositions/bulk', {
        product_id: this.composition_product.id,
        items: this.composition_rows,
      }).then(function () {
        self.composition_saving = false
        self.dialog_composition = false
        self.flashMessage.success({ title: 'Succès', message: 'Composition enregistrée' })
      }).catch(function () { self.composition_saving = false })
    },
    close_composition() {
      this.dialog_composition = false
      this.composition_product = null
      this.composition_rows = []
    },
  },
  mounted() { this.load(); this.getCategories(); this.getStore(); this.reset() },
}
</script>

<style scoped>
.search-input {
  width: 260px;
  padding: 8px 12px;
  font-size: 0.8125rem;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  outline: none;
  color: #1a2332;
  background: #fff;
}
.search-input:focus {
  border-color: #194173;
  box-shadow: 0 0 0 2px rgba(25, 65, 115, 0.1);
}
.search-input::placeholder { color: #94a3b8; }

.table-card {
  border: 1px solid #e8ecf1 !important;
  border-radius: 8px !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04) !important;
}

.product-img {
  width: 36px;
  height: 36px;
  border-radius: 6px;
  border: 1px solid #e8ecf1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: pointer;
  background: #f8fafc;
}
.product-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.product-img:hover {
  border-color: #194173;
}

.dialog-card { border-radius: 12px !important; }
.dialog-title { font-size: 0.9375rem !important; font-weight: 600 !important; padding: 14px 20px 12px !important; }
.dialog-body { padding: 20px 20px 8px !important; }
.dialog-actions { padding: 8px 16px 12px !important; }

.form-group {
  margin-bottom: 16px;
}

.form-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #475569;
  margin-bottom: 4px;
}

.usd-sync-hint {
  font-size: 0.8125rem;
  color: #64748b;
  line-height: 1.45;
  margin: 0 0 12px 0;
  padding: 10px 12px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e8ecf1;
}

.product-name-with-barcode {
  cursor: help;
  border-bottom: 1px dotted rgba(25, 65, 115, 0.35);
}
</style>

<style>
/* Tooltip Vuetify rendu en téléport : styles globaux pour la boîte code-barres */
.barcode-tooltip-outer {
  padding: 0 !important;
  opacity: 1 !important;
  background: transparent !important;
  box-shadow: none !important;
}
.barcode-tooltip-inner {
  padding: 8px 12px;
  background: #1a2332;
  color: #f1f5f9;
  border-radius: 6px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.barcode-tooltip-label {
  display: block;
  font-size: 0.625rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 4px;
}
.barcode-tooltip-value {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
}
</style>
